import React, { useEffect, useState } from "react";
import {
  CaretLeft,
  CaretRight,
  X,
} from "@phosphor-icons/react";

import { documents as documentsApi } from "@/api";
import { fileUrl, formatApiError } from "@/lib/api";

/**
 * Prévisualisation interne d'un document.
 *
 * Ce composant utilise les routes authentifiées Documents :
 * il ne déclenche donc aucun événement de lecture côté partage public.
 */
export default function DocumentPreviewModal({ doc, onClose }) {
  const url = fileUrl(doc.id);
  const ext = (doc.extension || "").toLowerCase();

  const isImage = [
    "png",
    "jpg",
    "jpeg",
    "gif",
    "webp",
    "svg",
  ].includes(ext);

  const isPdf = ext === "pdf";
  const isHtml = ["html", "htm"].includes(ext);

  const isSpreadsheet = [
    "xls",
    "xlsx",
  ].includes(ext);

  const isPagedOffice = [
    "doc",
    "docx",
    "ppt",
    "pptx",
  ].includes(ext);

  const isOffice =
    isSpreadsheet || isPagedOffice;

  const isText = [
    "txt",
    "csv",
    "json",
    "xml",
    "md",
    "log",
  ].includes(ext);

  const [previewLoading, setPreviewLoading] =
    useState(isOffice);

  const [previewError, setPreviewError] =
    useState("");

  const [pageCount, setPageCount] =
    useState(0);

  const [currentPage, setCurrentPage] =
    useState(1);

  const [pageLoading, setPageLoading] =
    useState(false);

  const [pageUrl, setPageUrl] =
    useState("");

  const [sheetHtml, setSheetHtml] =
    useState("");

  useEffect(() => {
    if (!isOffice) {
      return undefined;
    }

    let cancelled = false;

    setPreviewLoading(true);
    setPreviewError("");
    setPageCount(0);
    setCurrentPage(1);
    setPageLoading(false);
    setSheetHtml("");

    documentsApi
      .getPreview(doc.id)
      .then((result) => {
        if (cancelled) {
          return;
        }

        const count = Number(
          result?.pageCount || 0
        );

        if (!count) {
          throw new Error(
            "Aucune page n’a été générée."
          );
        }

        setPageCount(count);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setPreviewError(
          formatApiError(
            error.response?.data?.detail
          ) ||
            "La génération de l’aperçu a échoué."
        );
      })
      .finally(() => {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [doc.id, isOffice]);

  /*
   * Word / PowerPoint :
   * on conserve strictement le fonctionnement actuel,
   * c'est-à-dire une page JPEG issue du PDF LibreOffice.
   */
  useEffect(() => {
    if (
      !isPagedOffice ||
      !pageCount ||
      currentPage < 1
    ) {
      return undefined;
    }

    let cancelled = false;
    let objectUrl = "";

    setPageLoading(true);
    setPreviewError("");

    documentsApi
      .getPreviewPage(
        doc.id,
        currentPage
      )
      .then((blob) => {
        if (cancelled) {
          return;
        }

        objectUrl =
          window.URL.createObjectURL(blob);

        setPageUrl((previousUrl) => {
          if (previousUrl) {
            window.URL.revokeObjectURL(
              previousUrl
            );
          }

          return objectUrl;
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setPreviewError(
          formatApiError(
            error.response?.data?.detail
          ) ||
            "Cette diapositive ne peut pas être affichée."
        );
      })
      .finally(() => {
        if (!cancelled) {
          setPageLoading(false);
        }
      });

    return () => {
      cancelled = true;

      if (objectUrl) {
        window.URL.revokeObjectURL(
          objectUrl
        );
      }
    };
  }, [
    currentPage,
    doc.id,
    isPagedOffice,
    pageCount,
  ]);

  /*
   * Excel :
   * une page de l'aperçu correspond maintenant exactement
   * à un onglet visible du classeur.
   */
  useEffect(() => {
    if (
      !isSpreadsheet ||
      !pageCount ||
      currentPage < 1
    ) {
      return undefined;
    }

    let cancelled = false;

    setPageLoading(true);
    setPreviewError("");
    setSheetHtml("");

    documentsApi
      .getPreviewSheet(
        doc.id,
        currentPage
      )
      .then((content) => {
        if (cancelled) {
          return;
        }

        if (
          typeof content !== "string" ||
          !content.trim()
        ) {
          throw new Error(
            "La feuille Excel est vide."
          );
        }

        setSheetHtml(content);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setPreviewError(
          formatApiError(
            error.response?.data?.detail
          ) ||
            "Cette feuille Excel ne peut pas être affichée."
        );
      })
      .finally(() => {
        if (!cancelled) {
          setPageLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    doc.id,
    isSpreadsheet,
    pageCount,
  ]);

  useEffect(
    () => () => {
      if (pageUrl) {
        window.URL.revokeObjectURL(
          pageUrl
        );
      }
    },
    [pageUrl]
  );

  const showPreviousPage = () => {
    setCurrentPage((page) =>
      Math.max(1, page - 1)
    );
  };

  const showNextPage = () => {
    setCurrentPage((page) =>
      Math.min(pageCount, page + 1)
    );
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-6"
      style={{
        background: "rgba(5, 10, 24, 0.7)",
        backdropFilter: "blur(6px)",
      }}
      onClick={onClose}
      data-acta-modal-kind="viewer"
      data-testid="preview-modal"
    >
      <div
        className={`au-card-solid w-full ${
          isSpreadsheet
            ? "max-w-[96vw]"
            : "max-w-5xl"
        } h-[88vh] flex flex-col rounded-[14px] overflow-hidden`}
        onClick={(event) =>
          event.stopPropagation()
        }
      >
        <div
          className="h-12 px-4 flex items-center justify-between shrink-0"
          style={{
            borderBottom:
              "1px solid var(--do-border)",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="label-eyebrow">
              Aperçu
            </div>

            <div
              className="font-heading font-semibold text-sm truncate"
              style={{
                color: "var(--do-ink)",
              }}
            >
              {doc.name}
            </div>

            {isHtml && (
              <span className="lirah-badge lirah-badge-primary">
                HTML interactif
              </span>
            )}

            {isOffice && pageCount > 0 && (
              <span
                className="text-xs"
                style={{
                  color: "var(--do-muted)",
                }}
                data-testid="preview-page-counter"
              >
                {currentPage} / {pageCount}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <a
              href={url}
              download={doc.name}
              className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded-md transition-colors"
              style={{
                color: "var(--do-muted)",
                border:
                  "1px solid var(--do-border-strong)",
              }}
              data-testid="preview-download"
              title="Télécharger"
            >
              Télécharger
            </a>

            <button
              onClick={onClose}
              className="p-1.5 rounded-md transition-colors hover:bg-cobalt-500/10"
              style={{
                color: "var(--do-muted)",
              }}
              data-testid="preview-close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div
          className="flex-1 overflow-hidden relative"
          style={{
            background: "var(--do-bg-2)",
          }}
        >
          {isPdf && (
            <iframe
              src={url}
              title={doc.name}
              className="w-full h-full"
              style={{
                border: "none",
                background: "white",
              }}
            />
          )}

          {isHtml && (
            <iframe
              src={url}
              title={doc.name}
              className="w-full h-full"
              style={{
                border: "none",
                background: "white",
              }}
              sandbox="allow-scripts allow-same-origin allow-forms"
            />
          )}

          {isImage && (
            <div className="w-full h-full flex items-center justify-center p-4">
              <img
                src={url}
                alt={doc.name}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}

          {isText && (
            <iframe
              src={url}
              title={doc.name}
              className="w-full h-full"
              style={{
                border: "none",
                background: "white",
              }}
            />
          )}

          {isOffice && (
            <div
              className="h-full flex flex-col"
              data-testid="office-preview-viewer"
            >
              <div
                className={
                  isSpreadsheet
                    ? "flex-1 min-h-0 overflow-hidden"
                    : "flex-1 min-h-0 overflow-auto flex items-center justify-center p-4"
                }
              >
                {previewLoading && (
                  <div
                    className={
                      isSpreadsheet
                        ? "h-full flex items-center justify-center text-center"
                        : "text-center"
                    }
                    data-testid="preview-generating"
                  >
                    <div>
                      <div className="label-eyebrow lirah-pulse">
                        Génération de l’aperçu…
                      </div>

                      <p
                        className="mt-3 text-sm"
                        style={{
                          color: "var(--do-muted)",
                        }}
                      >
                        {isSpreadsheet
                          ? "Préparation des feuilles du classeur…"
                          : (
                            <>
                              Les présentations volumineuses
                              peuvent demander plusieurs
                              minutes lors de la première
                              ouverture.
                            </>
                          )}
                      </p>
                    </div>
                  </div>
                )}

                {!previewLoading &&
                  pageLoading && (
                    <div
                      className={
                        isSpreadsheet
                          ? "h-full flex items-center justify-center"
                          : ""
                      }
                    >
                      <div className="label-eyebrow lirah-pulse">
                        {isSpreadsheet
                          ? "Chargement de la feuille…"
                          : "Chargement de la diapositive…"}
                      </div>
                    </div>
                  )}

                {!previewLoading &&
                  !pageLoading &&
                  previewError && (
                    <div
                      className={
                        isSpreadsheet
                          ? "h-full flex items-center justify-center p-4"
                          : ""
                      }
                    >
                      <div
                        className="max-w-md text-center"
                        data-testid="preview-error"
                      >
                        <p className="font-semibold">
                          Aperçu indisponible
                        </p>

                        <p
                          className="mt-2 text-sm"
                          style={{
                            color:
                              "var(--do-muted)",
                          }}
                        >
                          {previewError}
                        </p>

                        <a
                          href={url}
                          download={doc.name}
                          className="inline-flex mt-4 px-3 py-2 rounded-md text-sm font-semibold"
                          style={{
                            border:
                              "1px solid var(--do-border-strong)",
                          }}
                        >
                          Télécharger le fichier
                        </a>
                      </div>
                    </div>
                  )}

                {isSpreadsheet &&
                  !previewLoading &&
                  !pageLoading &&
                  !previewError &&
                  sheetHtml && (
                    <iframe
                      srcDoc={sheetHtml}
                      title={`Feuille Excel ${currentPage} de ${doc.name}`}
                      className="w-full h-full"
                      sandbox=""
                      style={{
                        border: "none",
                        background: "white",
                      }}
                      data-testid="preview-spreadsheet-sheet"
                    />
                  )}

                {isPagedOffice &&
                  !previewLoading &&
                  !pageLoading &&
                  !previewError &&
                  pageUrl && (
                    <img
                      src={pageUrl}
                      alt={`Diapositive ${currentPage} de ${doc.name}`}
                      className="max-w-full max-h-full object-contain bg-[var(--do-bg-solid)] shadow-lg"
                      data-testid="preview-page-image"
                    />
                  )}
              </div>

              {pageCount > 1 && (
                <div
                  className="h-14 shrink-0 flex items-center justify-center gap-4 px-4"
                  style={{
                    borderTop:
                      "1px solid var(--do-border)",
                  }}
                >
                  <button
                    type="button"
                    onClick={showPreviousPage}
                    disabled={
                      currentPage <= 1 ||
                      pageLoading
                    }
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md disabled:opacity-40"
                    style={{
                      border:
                        "1px solid var(--do-border-strong)",
                    }}
                    data-testid="preview-previous-page"
                  >
                    <CaretLeft size={16} />
                    Précédente
                  </button>

                  <span
                    className="text-sm font-semibold"
                    style={{
                      color: "var(--do-ink)",
                    }}
                  >
                    {isSpreadsheet
                      ? (
                        <>
                          Feuille {currentPage} sur{" "}
                          {pageCount}
                        </>
                      )
                      : (
                        <>
                          Diapositive {currentPage} sur{" "}
                          {pageCount}
                        </>
                      )}
                  </span>

                  <button
                    type="button"
                    onClick={showNextPage}
                    disabled={
                      currentPage >= pageCount ||
                      pageLoading
                    }
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md disabled:opacity-40"
                    style={{
                      border:
                        "1px solid var(--do-border-strong)",
                    }}
                    data-testid="preview-next-page"
                  >
                    Suivante
                    <CaretRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}

          {!isPdf &&
            !isHtml &&
            !isImage &&
            !isOffice &&
            !isText && (
              <iframe
                src={url}
                title={doc.name}
                className="w-full h-full"
                style={{
                  border: "none",
                  background: "white",
                }}
              />
            )}
        </div>
      </div>
    </div>
  );
}
